import React, { useState } from 'react';
import { DocumentNode } from 'graphql';

import AvailabilityScheduleEditView from 'coaching/components/CoachAvailabilityView/AvailabilityScheduleEditView';
import CoachAvailabilitySchedulesList, {
  ExactlyOneOrTwoSchedules,
} from 'coaching/components/CoachAvailabilityView/AvailabilitySchedulesList';
import CoachAvailabilitiesByDayList from 'coaching/components/CoachAvailabilityView/CoachAvailabilitiesByDayList';
import {
  CoachingAvailabilitySchedule,
  SingleDayCoachAvailability,
  CoachPause,
} from 'coaching/containers/CoachAvailabilityContainer';
import Display from 'components/Display';
import MediaObject from 'components/MediaObject';
import PauseTutoringView from 'coaching/components/CoachAvailabilityView/PauseTutoringView';
import Row from 'components/Row';

type ViewMode = 'view' | 'edit' | 'pause';

export type Props = {
  schedules: CoachingAvailabilitySchedule[];
  coachTimezone: string;
  availabilities: SingleDayCoachAvailability[];
  weekOffset: number;
  weekStartDate: string;
  weekEndDate: string;
  onWeekForward: () => void;
  onWeekBack: () => void;
  availabilityQuery: { query: DocumentNode; variables?: Object };
  pause?: CoachPause;
};

export default function CoachAvailabilityView(props: Props) {
  const {
    schedules,
    coachTimezone,
    availabilities,
    weekOffset,
    weekStartDate,
    weekEndDate,
    onWeekForward,
    onWeekBack,
    availabilityQuery,
    pause,
  } = props;

  // if a coach has no existing schedules, we force them into edit mode
  // so that they can create their first schedule
  const hasSchedules = schedules.length > 0;
  const defaultViewMode = hasSchedules ? 'view' : 'edit';
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [scheduleBeingEdited, setScheduleBeingEdited] = useState<
    CoachingAvailabilitySchedule | undefined
  >();

  function resetView() {
    setScheduleBeingEdited(undefined);
    setViewMode('view');
  }

  const firstTwoSchedules: ExactlyOneOrTwoSchedules | undefined = hasSchedules
    ? [schedules[0], schedules[1] ?? null]
    : undefined;
  const viewComponents: [React.ReactElement, React.ReactElement] = [
    <Row size="small">
      <CoachAvailabilitySchedulesList
        schedules={firstTwoSchedules}
        onClickSchedule={schedule => {
          setScheduleBeingEdited(schedule);
          setViewMode('edit');
        }}
        onClickSetUpcomingSchedule={() => setViewMode('edit')}
        onClickPauseTutoring={() => setViewMode('pause')}
        timezone={coachTimezone}
        pause={pause}
      />
    </Row>,
    <CoachAvailabilitiesByDayList
      availabilities={availabilities}
      weekOffset={weekOffset}
      weekStartDate={weekStartDate}
      weekEndDate={weekEndDate}
      onWeekForward={onWeekForward}
      onWeekBack={onWeekBack}
      timezone={coachTimezone}
      availabilityQuery={availabilityQuery}
    />,
  ];
  const mapViewModeToComponent: Record<ViewMode, React.ReactElement> = {
    view: (
      <>
        <Display hide="480">
          <MediaObject>{viewComponents}</MediaObject>
        </Display>
        <Display show="480">{viewComponents}</Display>
      </>
    ),
    edit: (
      <AvailabilityScheduleEditView
        isFirstSchedule={!hasSchedules}
        scheduleBeingEdited={scheduleBeingEdited}
        initialTimezone={coachTimezone}
        onSubmit={resetView}
        onBack={hasSchedules ? resetView : undefined}
        availabilityQuery={availabilityQuery}
      />
    ),
    pause: (
      <PauseTutoringView
        onBack={resetView}
        onSubmit={resetView}
        pause={pause}
        availabilityQuery={availabilityQuery}
      />
    ),
  };
  return mapViewModeToComponent[viewMode];
}
